import { useEffect, useMemo, useState } from 'react';
import { Card, EmptyState, FilterBar, PageHeader } from '@/components/ui';
import { Icon } from '@/components/ui/Icon';
import { PhoneCell } from '@/components/telephony/PhoneCell';
import { agentApi, type AgentLead } from '@/shared/api/agentApi';
import { leadsByPerson, type PersonResult } from '../lib/leadSearch';
import { useSipStore } from '../stores/sipStore';
import { toast } from '@/components/ui/Toast';

/** מתחת לזה החיפוש לא נשלח — שאילתה של תו אחד מחזירה חצי מאגר. */
const MIN_QUERY = 2;

/**
 * "חיפוש" (אפיון 5.6) — חיפוש לפי טלפון או שם, תוצאה אחת לאדם גם אם הוא
 * מופיע בכמה קמפיינים, ורק בקמפיינים של הנציג עצמו (הסינון לקמפיינים
 * שלו נעשה בשרת; כאן רק מציגים).
 */
export function LeadSearch() {
  const [q, setQ] = useState('');
  const [leads, setLeads] = useState<AgentLead[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const dial = useSipStore((s) => s.dial);
  const registered = useSipStore((s) => s.registered);

  // תוצאה אחת לאדם — גם אם השרת החזיר שורה לכל קמפיין.
  const people = useMemo<PersonResult[]>(() => leadsByPerson(leads), [leads]);

  useEffect(() => {
    const term = q.trim();
    if (term.length < MIN_QUERY) {
      setLeads([]);
      setSearched(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    // דיבאונס — הקלדה מהירה לא שולחת בקשה לכל תו.
    const timer = setTimeout(() => {
      void agentApi.searchLeads(term)
        .then((d) => { if (!cancelled) { setLeads(d); setSearched(true); } })
        .catch(() => {
          if (cancelled) return;
          setLeads([]);
          setSearched(true);
          toast('שגיאה בחיפוש', 'danger');
        })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [q]);

  async function call(phone: string) {
    if (!registered) {
      toast('הסופטפון לא מחובר — המתן לחיבור לפני חיוג', 'danger');
      return;
    }
    try {
      await dial(phone);
    } catch {
      toast('שגיאה בחיוג', 'danger');
    }
  }

  return (
    <div className="stack" style={{ padding: 'var(--gap)' }}>
      <PageHeader title="חיפוש" />
      <FilterBar search={q} onSearch={setQ} onClear={() => setQ('')} />

      {loading && <Card>מחפש…</Card>}

      {!loading && q.trim().length < MIN_QUERY && (
        <EmptyState icon="search" title="חיפוש לפי שם או טלפון" description="הקלד לפחות שני תווים." />
      )}

      {!loading && searched && people.length === 0 && (
        <EmptyState icon="search" title="לא נמצאו תוצאות" description="נסה שם או מספר טלפון אחר." />
      )}

      {!loading && people.map((person) => (
        <Card key={person.key}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 'var(--t-lg)', fontWeight: 600 }}>
                {person.name || '(ללא שם)'}
              </div>
              <div style={{ marginTop: 2 }}>
                <PhoneCell number={person.phone} size="sm" />
              </div>
              {/* אותו אדם בכמה קמפיינים — שורה אחת, והקמפיינים כתגיות. */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                {person.leads.map((lead) => (
                  <span
                    key={lead.id}
                    style={{
                      fontSize: 'var(--t-xs)', color: 'var(--text-2)',
                      background: 'var(--surface-2)', borderRadius: 'var(--r-1)',
                      padding: '2px 8px', whiteSpace: 'nowrap',
                    }}
                  >
                    {lead.campaign_name || `קמפיין ${lead.campaign_id}`}
                  </span>
                ))}
              </div>
            </div>
            <button type="button" className="btn primary" onClick={() => void call(person.phone)}>
              <Icon name="phone" size={14} /> חייג
            </button>
          </div>
        </Card>
      ))}
    </div>
  );
}
